/**
 * Client-side validation for the client form (PX-W6.2) — hand-rolled, no new
 * dependencies. Runs BEFORE submit; the server remains the authority and its
 * mapped errors (409 `client.duplicate` …) surface inline beside these.
 *
 * The returned map's VALUES are i18n keys (rendered with the `clients`
 * namespace default), so no message text lives in code.
 */

import type { ClientInput } from './types'

/** A well-formed e-mail: one @, non-blank local and domain, dotted TLD. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Field key → i18n message key (namespaced with the `ns:key` colon form so
 * any `t()` resolves it regardless of the caller's active namespace). Empty
 * map means the input may be submitted.
 */
export function validateClientInput(input: ClientInput): Record<string, string> {
  const errors: Record<string, string> = {}

  if (input.name.trim().length === 0) {
    errors['name'] = 'clients:validation.nameRequired'
  }

  const { contacts = [] } = input
  contacts.forEach((contact, index) => {
    const email = contact.email?.trim() ?? ''
    if (email.length > 0 && !EMAIL_PATTERN.test(email)) {
      errors[`contacts.${index}.email`] = 'clients:validation.contactEmailInvalid'
    }
  })

  return errors
}

/** The same email rule, exported for the invite form's reuse. */
export const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim())
